/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { yearToCropYearFull, OLAPTransformerResult, castUnit, GLOBAL_UNIT } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { orderBy } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "attributes.default";

export function useChart1(data: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();

  const categories = useMemo(
    () =>
      data &&
      data.columns.map((x) => yearToCropYearFull(x.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name)),
    [data],
  );

  const options = useChartOptions({}, [
    chartPipe("column"),
    legendPipe("top", {
      labelFormatter: function() {
        const { name } = this;
        switch (name) {
          case "Production":
            return name + " (million mt)";
          case "Area Harvested":
            return name + " (million ha)";
          case "Yield":
            return name + " (mt/ha)";
          default:
            return name;
        }
      },
    }),
    addXAxisPipe(categories),
    addYAxisPipe({
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
    addYAxisPipe({
      opposite: true,
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 2);
          }
          return label;
        },
      },
    }),
    tooltipPipe((serie) => {
      switch (serie) {
        case "Production":
          return "million mt";
        case "Area Harvested":
          return "million ha";
        case "Yield":
          return "mt/ha";
        default:
          return "";
      }
    }),
  ]);

  useEffect(() => {
    const getPointHint = (data, pointIndex: number) => {
      return data.rows.map((row) => {
        const title = row.attributes[`${SERIES_AXIS}.name`][9];
        const value = row.points[pointIndex]?.value;
        const formattedValue =
          row.selection[SERIES_AXIS] === 22
            ? castUnit(value, GLOBAL_UNIT.YIELD_MTHA, GLOBAL_UNIT.DISPLAY_YIELD) || 0
            : castUnit(value, GLOBAL_UNIT.AREA_MHA, GLOBAL_UNIT.AREA_MHA) || 0;
        return { title, value: formattedValue };
      });
    };

    if (data) {
      const columnSeries = data.rows.map<SeriesOptionsType>((row) => {
        if (row.selection[SERIES_AXIS] === 22) {
          return {
            type: "line",
            yAxis: 1,
            dashStyle: "Dash",
            color: "#005897",
            showInLegend: true,
            dataLabels: {
              color: "#005897",
              enabled: true,
              formatter: function() {
                return Highcharts.numberFormat(this.y || 0, 1);
              },
            },
            lineWidth: 2,
            marker: {
              symbol: "diamond",
            },
            states: {
              hover: {
                lineWidth: 0,
                lineWidthPlus: 0,
              },
            },
            name: row.attributes[`${SERIES_AXIS}.name`] && row.attributes[`${SERIES_AXIS}.name`][9],
            id: row.attributes[`${SERIES_AXIS}.name`] && row.attributes[`${SERIES_AXIS}.name`][9],
            data: row.points.map((p, i) => ({
              y: castUnit(p.value, GLOBAL_UNIT.YIELD_MTHA, GLOBAL_UNIT.YIELD_MTHA) || 0,
              hintInfo: getPointHint(data, i),
            })),
          };
        }
        return {
          type: "column",
          yAxis: 0,
          showInLegend: true,
          dataLabels: {
            enabled: true,
            inside: true,
            formatter: function() {
              return Highcharts.numberFormat(this.y || 0, 0);
            },
          },
          name: row.attributes[`${SERIES_AXIS}.name`] && row.attributes[`${SERIES_AXIS}.name`][9],
          id: row.attributes[`${SERIES_AXIS}.name`] && row.attributes[`${SERIES_AXIS}.name`][9],
          data: orderBy(row.points, (p) => {
            return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
          }).map((p, i) => ({
            y: castUnit(p.value, GLOBAL_UNIT.AREA_MHA, GLOBAL_UNIT.AREA_MHA) || 0,
            hintInfo: getPointHint(data, i),
          })), // TODO: fix
        };
      });

      const result: Highcharts.Options = {
        ...options,
        colors: ["#D9D10C", "#1CA955"],
        series: columnSeries,
      };

      setState(result);
    }
  }, [data]);
  return state;
}
